import {
    ref, computed, watch,
} from 'vue';
import { useAntennaUsageStore } from '@/apps/company-admin/stores/statistics/antennaUsageStore';
import { useExternalApiUsageStore } from '@/apps/company-admin/stores/statistics/externalApiStore';
import { useEpcRegistrationStore } from '@/apps/company-admin/stores/statistics/epcRegistrationStore';
import { useErosStatisticsStore } from '@/apps/company-admin/stores/statistics/erosStatisticsStore';
import { usePaymentStatisticsStore } from '@/apps/company-admin/stores/statistics/paymentStatisticsStore';
import { useProductStatisticsStore } from '@/apps/company-admin/stores/statistics/productStatisticsStore';

type TabStoreType =
    | ReturnType<typeof useAntennaUsageStore>
    | ReturnType<typeof useExternalApiUsageStore>
    | ReturnType<typeof useEpcRegistrationStore>
    | ReturnType<typeof useErosStatisticsStore>
    | ReturnType<typeof usePaymentStatisticsStore>
    | ReturnType<typeof useProductStatisticsStore>;

const STORAGE_KEY = 'statistics-filter-expanded';
const isExpanded = ref(localStorage.getItem(STORAGE_KEY) === 'true');
const filterBarHeight = ref(56);
const filterBarElement = ref<Element | null>(null);
let globalResizeObserver: ResizeObserver | null = null;

watch(isExpanded, newValue => {
    localStorage.setItem(STORAGE_KEY, String(newValue));
});

export const useFilterBar = () => {
    const getStoreByTabName = (tabName: string): TabStoreType => {
        switch (tabName) {
        case 'eros':
            return useErosStatisticsStore();
        case 'payments':
            return usePaymentStatisticsStore();
        case 'products':
            return useProductStatisticsStore();
        case 'antenna-usage':
            return useAntennaUsageStore();
        case 'external-api-usage':
            return useExternalApiUsageStore();
        case 'epc-registration':
            return useEpcRegistrationStore();
        default:
            return useErosStatisticsStore();
        }
    };

    const removeFilter = (tabName: string, filterKey: string): void => {
        const store = getStoreByTabName(tabName);
        store.removeFilter(filterKey);
    };

    const resetAllFilters = (tabName: string): void => {
        const store = getStoreByTabName(tabName);
        store.resetFilters();
    };

    const toggleExpanded = (): void => {
        isExpanded.value = !isExpanded.value;
    };

    const expand = (): void => {
        isExpanded.value = true;
    };

    const collapse = (): void => {
        isExpanded.value = false;
    };

    const getFilterConfig = (tabName: string) => {
        const configs = {
            eros: {
                commonFilters: ['dateRange', 'grouping'],
                specificFilters: ['erosConfigurationId'],
            },
            payments: {
                commonFilters: ['dateRange', 'grouping'],
                specificFilters: ['paymentConfigurationId'],
            },
            products: {
                commonFilters: ['dateRange', 'grouping'],
                specificFilters: ['productIds', 'warehouseNodeIds'],
            },
            'antenna-usage': {
                commonFilters: ['dateRange', 'grouping'],
                specificFilters: ['antennaIds', 'locationIds'],
            },
            'external-api-usage': {
                commonFilters: ['dateRange', 'grouping'],
                specificFilters: ['endpoints', 'statusCodes', 'userIds'],
            },
            'epc-registration': {
                commonFilters: ['dateRange', 'grouping'],
                specificFilters: ['productIds', 'categoryIds'],
            },
        };

        return configs[tabName as keyof typeof configs] || configs.eros;
    };

    const observeFilterBarHeight = (element: Element): (() => void) => {
        filterBarElement.value = element;

        if (globalResizeObserver) {
            globalResizeObserver.disconnect();
        }

        globalResizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                filterBarHeight.value = entry.contentRect.height;
            }
        });

        globalResizeObserver.observe(element);

        return () => {
            if (globalResizeObserver) {
                globalResizeObserver.disconnect();
                globalResizeObserver = null;
            }
            filterBarElement.value = null;
        };
    };

    return {
        isExpanded: computed(() => isExpanded.value),
        filterBarHeight: computed(() => filterBarHeight.value),
        toggleExpanded,
        expand,
        collapse,
        removeFilter,
        resetAllFilters,
        getStoreByTabName,
        getFilterConfig,
        observeFilterBarHeight,
    };
};
